/* eslint-disable import/no-duplicates */
import { useMemo } from 'react';
import { parse } from 'date-fns';
import { format } from 'date-fns-tz';
import enUSLocale from 'date-fns/locale/en-US';

import {
  datesAreInSameMonth,
  datesAreInSameYear,
  datesAreOnSameDay,
} from '@/lib/dates';

interface DateRangeLabelProps {
  startsAtDate: string;
  endsAtDate: string;
  startsAtTime: string;
  endsAtTime: string;
  locale?: string;
  timeZone?: string;
}

const generateDateLabel = (startDate, endDate, locale) => {
  if (datesAreOnSameDay(startDate, endDate)) {
    return `${startDate.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })}`;
  }

  if (datesAreInSameMonth(startDate, endDate)) {
    const monthLabel = startDate.toLocaleString(locale, {
      month: 'long',
    });
    if (locale === 'en-US')
      return `${monthLabel} ${startDate.getDate()}–${endDate.getDate()} ${startDate.getFullYear()}`;
    return `${startDate.getDate()}–${endDate.getDate()} ${monthLabel} ${startDate.getFullYear()}`;
  }

  if (datesAreInSameYear(startDate, endDate)) {
    return `${startDate.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
    })} - ${endDate.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })}`;
  }

  return `${startDate.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })} - ${endDate.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`;
};

export const DateRangeLabel = ({
  startsAtDate,
  endsAtDate,
  startsAtTime,
  endsAtTime,
  locale = 'en-GB',

  // ET - default
  timeZone = 'America/New_York',
}: DateRangeLabelProps) => {
  const dateRange = useMemo(() => {
    // parse/date-substring ensures our date is always static to the server set date
    const startDate = parse(startsAtDate, 'yyyy-MM-dd', new Date());
    const endDate = parse(endsAtDate, 'yyyy-MM-dd', new Date());
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const formatOptions = {
      locale: enUSLocale,
      timeZone,
    };

    const timeZoneShort = format(startDate, 'zzz', formatOptions);
    const timeZoneLong = format(endDate, 'zzzz', formatOptions);

    const startTime = startsAtTime;
    const endTime = endsAtTime;

    const dateLabel = generateDateLabel(startDate, endDate, locale);

    return {
      dateLabel,
      startTime,
      endTime,
      timeZoneShort,
      timeZoneLong,
    };
  }, [startsAtDate, endsAtDate, timeZone, startsAtTime, endsAtTime, locale]);

  return (
    <span>
      <time>{dateRange.dateLabel}</time>
      {dateRange.startTime && (
        <>
          {' '}
          <time dateTime={dateRange.startTime}>{dateRange.startTime}</time>
        </>
      )}
      {dateRange.endTime && (
        <>
          -<time dateTime={dateRange.endTime}>{dateRange.endTime}</time>
        </>
      )}
      {dateRange.timeZoneShort && (
        <>
          {' '}
          (<abbr title={dateRange.timeZoneLong}>{dateRange.timeZoneShort}</abbr>
          )
        </>
      )}
    </span>
  );
};
